import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { getTour } from './registry';
import {
  tourStateKeys,
  useDeleteTourStateMutation,
  useTourStates,
  useUpsertTourStateMutation,
} from './hooks/useTourStates';
import type { TourDefinition, TourState, TourStep } from './types';
import { TourOverlay } from './components/TourOverlay';
import { TourContext, type ActiveTour, type TourContextValue } from './context';
import { useQueryClient } from '@tanstack/react-query';

export type { TourContextValue } from './context';

interface TourProviderProps {
  children: ReactNode;
}

function tourVersion(def: TourDefinition): number {
  return def.version ?? 1;
}

function makeFreshState(def: TourDefinition): TourState {
  return {
    tourId: def.id,
    version: tourVersion(def),
    completed: false,
    dismissed: false,
    lastStepIndex: 0,
    updatedAt: new Date().toISOString(),
    featureFlagKey: def.featureFlagKey ?? null,
  };
}

/**
 * Wait for `step.waitFor` to resolve true (or until `timeoutMs` elapses).
 * Used to pause briefly when a step depends on an async UI transition.
 */
async function waitForStep(step: TourStep, timeoutMs = 2000): Promise<void> {
  if (!step.waitFor) return;
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    try {
      const ready = await step.waitFor();
      if (ready) return;
    } catch {
      // ignore — try again until timeout
    }
    await new Promise((r) => setTimeout(r, 80));
  }
}

/**
 * Walk through the tour's step list starting at ``from`` in ``direction``
 * (1 = forward, -1 = backward), skipping any step whose ``skipIf()``
 * returns truthy. Returns the resolved index or ``null`` when we walk off
 * the end of the list (which means "complete the tour" when going forward,
 * or "stay where you are" when going backward).
 */
async function resolveStepIndex(
  def: TourDefinition,
  from: number,
  direction: 1 | -1,
): Promise<number | null> {
  let i = from;
  while (i >= 0 && i < def.steps.length) {
    const step = def.steps[i];
    let shouldSkip = false;
    if (step.skipIf) {
      try {
        shouldSkip = Boolean(await step.skipIf());
      } catch {
        // A throwing skipIf means "I don't know" — treat as "don't skip"
        // so the user still sees the step rather than silently losing it.
        shouldSkip = false;
      }
    }
    if (!shouldSkip) return i;
    i += direction;
  }
  return null;
}

export function TourProvider({ children }: TourProviderProps) {
  const [active, setActive] = useState<ActiveTour | null>(null);

  // Server state lives in TanStack Query. Mutations update the cache
  // optimistically (via ``onMutate``) so synchronous reads from
  // ``hasSeen`` see the new state before the network round-trip lands.
  const queryClient = useQueryClient();
  const tourStatesQuery = useTourStates();
  const upsertMutation = useUpsertTourStateMutation();
  const deleteMutation = useDeleteTourStateMutation();

  // Build a synchronous lookup keyed by tour id, keeping the highest
  // version per tour. Bumping a tour's version is how authors say "show
  // this again" — older rows must not mask the current one.
  const stateMap = useMemo(() => {
    const map = new Map<string, TourState>();
    for (const s of tourStatesQuery.data ?? []) {
      const existing = map.get(s.tourId);
      if (!existing || s.version > existing.version) map.set(s.tourId, s);
    }
    return map;
  }, [tourStatesQuery.data]);

  // Refs hold the latest snapshot so async callbacks (advance, skip,
  // etc.) don't capture stale closures across step transitions.
  const stateMapRef = useRef(stateMap);
  stateMapRef.current = stateMap;
  const upsertRef = useRef(upsertMutation);
  upsertRef.current = upsertMutation;

  /**
   * Persist a tour state. The mutation's ``onMutate`` updates the cache
   * synchronously — the awaited ``mutateAsync`` only matters when the
   * caller wants to wait for the network round-trip (terminal saves).
   */
  const persist = useCallback(async (next: TourState): Promise<TourState> => {
    return upsertRef.current.mutateAsync(next);
  }, []);

  /**
   * Persist a terminal (completed or dismissed) state for ``def`` AND
   * propagate the same terminal flag to any tours listed in ``def.alsoMarks``.
   *
   * All saves run in parallel and are awaited so a fast reload doesn't
   * undo any of them.
   */
  const persistTerminal = useCallback(
    async (def: TourDefinition, primary: TourState): Promise<void> => {
      const ancillaries: TourState[] = (def.alsoMarks ?? [])
        .map((id) => getTour(id))
        .filter((d): d is TourDefinition => Boolean(d))
        .map((d) => ({
          tourId: d.id,
          version: tourVersion(d),
          completed: primary.completed,
          dismissed: primary.dismissed,
          lastStepIndex: 0,
          updatedAt: new Date().toISOString(),
          featureFlagKey: d.featureFlagKey ?? null,
        }));
      await Promise.all([primary, ...ancillaries].map(persist));
    },
    [persist],
  );

  const hasSeen = useCallback(
    (tourId: string) => {
      const s = stateMap.get(tourId);
      if (!s || (!s.completed && !s.dismissed)) return false;
      const def = getTour(tourId);
      const required = def?.version ?? 1;
      return s.version >= required;
    },
    [stateMap],
  );

  const startTour = useCallback<TourContextValue['startTour']>(
    async (tourId, opts) => {
      const def = getTour(tourId);
      if (!def) {
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.warn(`[tours] startTour("${tourId}") — no such tour registered`);
        }
        return;
      }
      // No-op if the same tour is already running. Without this, every
      // step transition triggers an optimistic cache update → ``hasSeen``
      // / ``startTour`` re-identify → page-level auto-start effects fire
      // again → ``setActive`` snaps the user back to ``lastStepIndex``.
      // ``force`` deliberately bypasses this so callers can intentionally
      // restart a tour mid-run.
      if (!opts?.force && activeRef.current?.definition.id === tourId) return;
      if (!opts?.force && hasSeen(tourId)) return;

      // Resume at the last step the user reached if mid-tour, else 0.
      const existing = stateMapRef.current.get(tourId);
      const resumeIndex =
        existing && !existing.completed && !existing.dismissed ? existing.lastStepIndex : 0;

      // Honor skipIf when picking the actual first visible step. If
      // every step is skipped we just mark the tour completed and bail.
      const startIndex = await resolveStepIndex(def, resumeIndex, 1);
      if (startIndex === null) {
        await persist({
          tourId: def.id,
          version: tourVersion(def),
          completed: true,
          dismissed: false,
          lastStepIndex: Math.max(0, def.steps.length - 1),
          updatedAt: new Date().toISOString(),
          featureFlagKey: def.featureFlagKey ?? null,
        });
        return;
      }

      const baseline: TourState = {
        ...(existing ?? makeFreshState(def)),
        version: tourVersion(def),
        completed: false,
        dismissed: false,
        lastStepIndex: startIndex,
        featureFlagKey: def.featureFlagKey ?? null,
      };
      // Fire-and-forget: the optimistic cache update inside ``onMutate``
      // happens synchronously, so we don't need to wait for the round-trip
      // before the tour overlay shows.
      void persist(baseline);

      const firstStep = def.steps[startIndex];
      if (firstStep) await waitForStep(firstStep);
      setActive({ definition: def, stepIndex: startIndex });
    },
    [hasSeen, persist],
  );

  // Use a ref so the async advance helpers always see the latest active
  // tour without re-creating the callbacks on every step change.
  const activeRef = useRef<ActiveTour | null>(null);
  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  // Prevent re-entrancy: a fast double-click on "Next" (or a poll firing
  // while ``onBeforeNext`` is still resolving) should not run side effects
  // twice or skip past a step.
  const advancingRef = useRef(false);

  /**
   * Internal advance helper. Public ``next()`` runs ``onBeforeNext`` first
   * (driving the UI on behalf of the user); the auto-advance poll calls it
   * with ``runOnBeforeNext=false`` because the user has already taken the
   * action manually.
   */
  const advance = useCallback(
    async (opts: { runOnBeforeNext: boolean }) => {
      if (advancingRef.current) return;
      const curr = activeRef.current;
      if (!curr) return;
      advancingRef.current = true;
      try {
        if (opts.runOnBeforeNext) {
          const currStep = curr.definition.steps[curr.stepIndex];
          if (currStep?.onBeforeNext) {
            try {
              await currStep.onBeforeNext();
            } catch (err) {
              if (import.meta.env.DEV) {
                // eslint-disable-next-line no-console
                console.warn(
                  `[tours] onBeforeNext for "${curr.definition.id}#${currStep.id}" threw — advancing anyway:`,
                  err,
                );
              }
            }
          }
        }

        const target = await resolveStepIndex(curr.definition, curr.stepIndex + 1, 1);
        const lastIndex = curr.definition.steps.length - 1;

        if (target === null) {
          // Terminal state: await the primary persist + any ancillary
          // ``alsoMarks`` persists so the backend definitely has the
          // completed flags before we drop the tour overlay.
          await persistTerminal(curr.definition, {
            tourId: curr.definition.id,
            version: tourVersion(curr.definition),
            completed: true,
            dismissed: false,
            lastStepIndex: lastIndex,
            updatedAt: new Date().toISOString(),
            featureFlagKey: curr.definition.featureFlagKey ?? null,
          });
          setActive(null);
          return;
        }

        // Intermediate step transition: fire-and-forget. The optimistic
        // cache update fires synchronously inside ``onMutate``; the next
        // step's ``persist`` will subsume this one even if the network
        // call doesn't land.
        void persist({
          tourId: curr.definition.id,
          version: tourVersion(curr.definition),
          completed: false,
          dismissed: false,
          lastStepIndex: target,
          updatedAt: new Date().toISOString(),
          featureFlagKey: curr.definition.featureFlagKey ?? null,
        });
        await waitForStep(curr.definition.steps[target]);
        setActive({ ...curr, stepIndex: target });
      } finally {
        advancingRef.current = false;
      }
    },
    [persist, persistTerminal],
  );

  const next = useCallback(() => advance({ runOnBeforeNext: true }), [advance]);

  // Poll ``autoAdvanceWhen`` while a step is active. Sequential loop (vs
  // ``setInterval``) so a slow predicate never spawns overlapping calls.
  // The 500 ms grace period lets the DOM settle, and the baseline check
  // ensures we treat the predicate as a *transition* signal — pre-existing
  // truthy state shouldn't trigger an auto-advance the user didn't earn.
  //
  // Important: every awaited predicate call is followed by an ``alive``
  // re-check before acting on the result. Async ``autoAdvanceWhen``
  // implementations (e.g. one that ``await``s on the DOM for a few seconds)
  // can outlive the effect's cleanup; without the post-await check, a
  // late-resolving truthy result could fire ``advance`` against the *new*
  // step and double-advance the tour.
  useEffect(() => {
    if (!active) return;
    const step = active.definition.steps[active.stepIndex];
    if (!step?.autoAdvanceWhen) return;

    let alive = true;
    const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

    const loop = async () => {
      await sleep(500);
      if (!alive) return;

      let baseline = false;
      try {
        baseline = Boolean(await step.autoAdvanceWhen!());
      } catch {
        return;
      }
      if (!alive) return;
      if (baseline) return; // pre-existing truthy state — wait for transition

      while (alive) {
        let result = false;
        try {
          result = Boolean(await step.autoAdvanceWhen!());
        } catch {
          // Ignore — try again next iteration.
        }
        if (!alive) return;
        if (result) {
          alive = false;
          void advance({ runOnBeforeNext: false });
          return;
        }
        await sleep(250);
      }
    };
    void loop();

    return () => {
      alive = false;
    };
  }, [active, advance]);

  const prev = useCallback(async () => {
    const curr = activeRef.current;
    if (!curr || curr.stepIndex === 0) return;

    const target = await resolveStepIndex(curr.definition, curr.stepIndex - 1, -1);
    if (target === null) return;

    setActive({ ...curr, stepIndex: target });
  }, []);

  const skip = useCallback<TourContextValue['skip']>(async () => {
    const curr = activeRef.current;
    if (!curr) return;
    await persistTerminal(curr.definition, {
      tourId: curr.definition.id,
      version: tourVersion(curr.definition),
      completed: false,
      dismissed: true,
      lastStepIndex: curr.stepIndex,
      updatedAt: new Date().toISOString(),
      featureFlagKey: curr.definition.featureFlagKey ?? null,
    });
    setActive(null);
  }, [persistTerminal]);

  const complete = useCallback<TourContextValue['complete']>(async () => {
    const curr = activeRef.current;
    if (!curr) return;
    await persistTerminal(curr.definition, {
      tourId: curr.definition.id,
      version: tourVersion(curr.definition),
      completed: true,
      dismissed: false,
      lastStepIndex: curr.definition.steps.length - 1,
      updatedAt: new Date().toISOString(),
      featureFlagKey: curr.definition.featureFlagKey ?? null,
    });
    setActive(null);
  }, [persistTerminal]);

  const resetTour = useCallback<TourContextValue['resetTour']>(
    async (tourId) => {
      const def = getTour(tourId);
      const version = def ? tourVersion(def) : 1;
      await deleteMutation.mutateAsync({ tourId, version });
      // Defensive: also clear from the cache in case the row had a
      // stored version different from the one we tried to delete.
      queryClient.setQueryData<TourState[]>(tourStateKeys.all(), (prev) =>
        (prev ?? []).filter((s) => s.tourId !== tourId),
      );
    },
    [deleteMutation, queryClient],
  );

  // ``hydrated`` means "we hold the backend's answer for the current
  // session": the query resolved with an actual state list. ``null`` data
  // (the fetch ran while unauthenticated) and query errors both leave us
  // *not* hydrated — auto-start logic in pages waits on this, so the safe
  // failure mode is "no tour" rather than re-prompting a user whose
  // completed/dismissed rows we simply haven't been able to read yet.
  const hydrated = tourStatesQuery.data != null;

  // TourProvider mounts at the app root, above the router outlet, so its
  // initial fetch usually fires on ``/login`` *before* a session exists.
  // Login/logout are SPA navigations (no reload), so nothing else would
  // ever refetch the ``staleTime: Infinity`` query — the pre-auth ``null``
  // (or a transient error) would mask the backend state for the entire
  // session. Recovery: after every route change, if we still don't hold
  // authenticated data, try the fetch again. Post-login this resolves on
  // the very first navigation into the app.
  const location = useLocation();
  const tourStatesQueryRef = useRef(tourStatesQuery);
  tourStatesQueryRef.current = tourStatesQuery;
  useEffect(() => {
    const query = tourStatesQueryRef.current;
    if (query.isFetching) return;
    if (query.data === null || query.isError) {
      void query.refetch();
    }
  }, [location.pathname]);

  const value = useMemo<TourContextValue>(
    () => ({ active, hydrated, startTour, next, prev, skip, complete, hasSeen, resetTour }),
    [active, hydrated, startTour, next, prev, skip, complete, hasSeen, resetTour],
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      {active && <TourOverlay tour={active.definition} stepIndex={active.stepIndex} />}
    </TourContext.Provider>
  );
}
